import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { parentActions } from "../slices/parent";
import { ParentGuardianMapper } from "../../mappers/ParentMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { ParentGuardianCreateRequestDto } from "../../shared/dtos/parentGuardian/ParentGuardianCreateRequestDto";
import { ParentGuardianResponseDto } from "../../shared/dtos/parentGuardian/ParentGuardianResponseDto";
import { ParentGuardianSummaryDto } from "../../shared/dtos/parentGuardian/ParentGuardianSummaryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { StudentParentGuardianLinkDto } from "../../shared/dtos/parentGuardian/StudentParentGuardianLinkDto";
import { UpdateParentGuardianPayload } from "../../shared/interfaces/parentGuardian/UpdateParentGuardianPayload";
export const createParent =
  (values: ParentGuardianCreateRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(parentActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ParentGuardianResponseDto>
      >("/parents", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Parent created successfully!");
        const listParentData = ParentGuardianMapper.mapParentToSummary(
          data.data,
        );
        dispatch(parentActions.createSuccess(listParentData));
        dispatch(
          parentActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(parentActions.setErrorCreate(message));
    }
  };

export interface ParentQueryParams extends PaginationQueryDto {
  branchId?: string;
}

export const getParents =
  (params: ParentQueryParams) => async (dispatch: AppDispatch) => {
    dispatch(parentActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<ParentGuardianSummaryDto>>
      >("/parents", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(parentActions.setItems(data.data.content));
        dispatch(parentActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(parentActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getParentById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(parentActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<ParentGuardianResponseDto>
    >(`/parents/${id}`, { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(parentActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(parentActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const updateParent =
  (payload: UpdateParentGuardianPayload) => async (dispatch: AppDispatch) => {
    dispatch(parentActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<ParentGuardianResponseDto>
      >(`/parents/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Parent updated successfully!");
        const listParentData = ParentGuardianMapper.mapParentToSummary(
          data.data,
        );
        dispatch(parentActions.updateSuccess(listParentData));
        dispatch(
          parentActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(parentActions.setErrorUpdate(message));
    }
  };

export const deleteParent = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(parentActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/parents/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Parent deleted successfully!");
      dispatch(parentActions.deleteSuccess(id));
      dispatch(
        parentActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(parentActions.setErrorDelete(message));
  }
};

export const linkStudentToParent =
  (values: StudentParentGuardianLinkDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(parentActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ParentGuardianResponseDto>
      >("/parents/link", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Student linked to parent successfully!");
        const summary = ParentGuardianMapper.mapParentToSummary(data.data);
        dispatch(parentActions.updateSuccess(summary));
        dispatch(
          parentActions.setServerStatus({
            operation: "link",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(parentActions.setErrorUpdate(message));
    }
  };

export const resetParentServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(parentActions.setServerStatus(null));
};
